import React, { useState } from 'react';
import { XMarkIcon, LinkIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  photoId: number;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, photoId }) => {
  const [shareLink, setShareLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expiresInHours, setExpiresInHours] = useState(0);

  const createShareLink = async () => {
    setLoading(true);
    try {
      const body: any = { photoId };
      if (expiresInHours > 0) {
        body.expiresInHours = expiresInHours;
      }

      const response = await fetch('/api/share/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        // Формируем URL через текущий хост (nginx)
        const currentHost = window.location.origin;
        setShareLink(`${currentHost}/share/${data.shareLink.token}`);
      } else {
        console.error('Failed to create share link');
      }
    } catch (error) {
      console.error('Error creating share link:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  React.useEffect(() => {
    if (isOpen && !shareLink) {
      createShareLink();
    }
  }, [isOpen]);

  // Обработка клавиши Esc для закрытия модального окна
  React.useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <LinkIcon className="h-5 w-5 mr-2" />
            Поделиться фотографией
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Expiration Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Срок действия ссылки
            </label>
            <select
              value={expiresInHours}
              onChange={(e) => {
                setExpiresInHours(Number(e.target.value));
                setShareLink('');
                // Автоматически создаем новую ссылку с новым сроком действия
                setTimeout(() => createShareLink(), 100);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value={0}>Без ограничения</option>
              <option value={1}>1 час</option>
              <option value={24}>1 день</option>
              <option value={168}>1 неделя</option>
              <option value={720}>1 месяц</option>
            </select>
          </div>

          {/* Share Link */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : shareLink ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Публичная ссылка
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                >
                  {copied ? (
                    <>
                      <CheckIcon className="h-4 w-4 mr-1" />
                      Скопировано
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                      Копировать
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Любой человек с этой ссылкой сможет просмотреть фотографию
                {expiresInHours > 0 && ` в течение ${expiresInHours === 1 ? '1 часа' :
                  expiresInHours === 24 ? '1 дня' :
                  expiresInHours === 168 ? '1 недели' :
                  expiresInHours === 720 ? '1 месяца' : `${expiresInHours} часов`}`}
              </p>
            </div>
          ) : null}

          {/* Generate New Link Button */}
          {shareLink && !loading && (
            <button
              onClick={() => {
                setShareLink('');
                createShareLink();
              }}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Создать новую ссылку
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;